import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles, BrainCircuit, Send, X, Calendar,
  ChevronDown, RotateCcw,
  Loader2, Printer, Copy, Check, Edit3, Save, FileText
} from 'lucide-react';
import { sendTeacherAdvisorChat } from './geminiService';
import { saveTeacherAiInteraction } from './aiLogService';

// PDF Çıktısı için metni temiz HTML'e dönüştürür
function textToPrintableHtml(text) {
  if (!text) return '';
  return text
    .split('\n')
    .map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '<br/>';
      if (trimmed.startsWith('### ')) {
        return `<h3 style="color:#ea580c; font-size:16px; margin:16px 0 6px 0; border-bottom:1px solid #fed7aa; padding-bottom:4px;">${trimmed.substring(4)}</h3>`;
      }
      if (trimmed.startsWith('## ')) {
        return `<h2 style="color:#c2410c; font-size:18px; margin:20px 0 8px 0; border-bottom:2px solid #ea580c; padding-bottom:6px;">${trimmed.substring(3)}</h2>`;
      }
      if (trimmed.startsWith('# ')) {
        return `<h1 style="color:#9a3412; font-size:20px; margin:24px 0 10px 0;">${trimmed.substring(2)}</h1>`;
      }
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        const itemText = trimmed.substring(2)
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
        return `<li style="margin-left:20px; margin-bottom:4px; line-height:1.5;">${itemText}</li>`;
      }
      if (/^\d+\.\s+/.test(trimmed)) {
        const itemText = trimmed
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
        return `<div style="font-weight:bold; margin-top:12px; margin-bottom:4px; color:#334155;">${itemText}</div>`;
      }
      const formatted = trimmed
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      return `<p style="margin:4px 0; line-height:1.6;">${formatted}</p>`;
    })
    .join('');
}

// Inline Markdown Biçimlendirici
const FormattedMessage = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');

  return (
    <div className="space-y-1.5 text-sm leading-relaxed text-slate-200">
      {lines.map((line, idx) => {
        const trimmed = line.trim();

        if (trimmed.startsWith('### ')) {
          return (
            <h4 key={idx} className="mt-3 mb-1 text-sm font-bold text-orange-400 flex items-center gap-1.5 border-b border-orange-500/20 pb-1">
              <span className="w-1.5 h-3.5 bg-orange-500 rounded-sm" />
              <span>{trimmed.replace(/^###\s*/, '')}</span>
            </h4>
          );
        }

        if (trimmed.startsWith('## ')) {
          return (
            <h3 key={idx} className="mt-4 mb-2 text-base font-bold text-white border-b border-slate-700 pb-1">
              {trimmed.replace(/^##\s*/, '')}
            </h3>
          );
        }

        if (/^\d+\.\s+/.test(trimmed)) {
          return (
            <div key={idx} className="mt-2.5 mb-1 font-bold text-amber-300 text-xs sm:text-sm">
              <span dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }} />
            </div>
          );
        }

        if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
          return (
            <div key={idx} className="flex items-start gap-2 ml-1 text-slate-200">
              <span className="text-orange-400 mt-1 text-xs">•</span>
              <span className="flex-1" dangerouslySetInnerHTML={{ __html: formatInline(trimmed.substring(2)) }} />
            </div>
          );
        }

        if (trimmed === '---') {
          return <hr key={idx} className="my-2 border-slate-700/60" />;
        }

        if (!trimmed) {
          return <div key={idx} className="h-1" />;
        }

        return (
          <p key={idx} dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
        );
      })}
    </div>
  );
};

function formatInline(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic text-slate-300">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-800 text-amber-300 px-1 py-0.5 rounded text-xs font-mono">$1</code>')
    .replace(/\$+/g, '');
}

export default function TeacherAiFloatingChat({ user, currentStudent, students = [], curriculum = [], db, isFirebaseActive }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [copyFeedbackIndex, setCopyFeedbackIndex] = useState(null);

  // Düzenleme Modalı Durumu (Öğretmen oluşturulan raporu düzenleyip PDF alabilir)
  const [editModalData, setEditModalData] = useState(null); // { index, text }

  const messagesEndRef = useRef(null);
  const textInputRef = useRef(null);

  const teacherName = user?.name || 'Uzm. Psk. Dan. Hamza Metehan KILIÇ';
  const storageKey = `kocum_teacher_ai_history_${user?.id || 'teacher'}`;

  // Geçmiş sohbetleri yükle
  useEffect(() => {
    if (!user?.id) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      }
    } catch (e) {
      console.warn("Öğretmen sohbet geçmişi yüklenemedi:", e);
    }
  }, [user?.id, storageKey]);

  // Sohbeti localStorage'a kaydet
  useEffect(() => {
    if (!user?.id) return;
    try {
      if (messages.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(messages));
      }
    } catch (e) {}
  }, [messages, user?.id, storageKey]);

  // Otomatik aşağı kaydırma
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setHasUnread(false);
    }
  }, [messages, isOpen, isLoading]);

  // Yeni Sohbet Tetikleme
  const handleResetClick = () => {
    if (messages.length > 0) {
      setShowConfirmModal(true);
    } else {
      executeResetChat();
    }
  };

  const executeResetChat = () => {
    setMessages([]);
    setInputText('');
    setShowConfirmModal(false);
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {}
  };

  // Mesaj Gönderme
  const handleSendMessage = async (textToSend = inputText) => {
    const text = (textToSend || '').trim();
    if (!text || isLoading) return;

    const userMsg = {
      role: 'user',
      text: text,
      timestamp: new Date().toISOString()
    };

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputText('');
    setIsLoading(true);

    try {
      // Son 8 mesajı göndererek hafıza ve bağlamı koru
      const apiHistory = newMessages.slice(-8);
      const reply = await sendTeacherAdvisorChat(apiHistory, {
        currentStudent,
        allStudents: students,
        curriculum,
        teacherName
      });

      const assistantMsg = {
        role: 'assistant',
        text: reply,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMsg]);
      if (!isOpen) setHasUnread(true);

      // Süperadmin için öğretmen kullanımını logla
      saveTeacherAiInteraction({
        teacher: user,
        userPrompt: text,
        aiResponse: reply,
        db,
        isFirebaseActive
      });
    } catch (error) {
      console.error("Öğretmen AI Danışmanı yanıt hatası:", error);
      const errMsg = {
        role: 'assistant',
        text: "Üzgünüm, şu anda yanıt oluşturulurken bir bağlantı hatası oluştu. Lütfen tekrar deneyiniz.",
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  // Metni Panoya Kopyalama
  const handleCopyText = (text, index) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedbackIndex(index);
      setTimeout(() => setCopyFeedbackIndex(null), 3000);
    });
  };

  // PDF Olarak Yazdır / İndir
  const handlePrintPdf = (content) => {
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Yazdırma penceresi açılamadı. Lütfen açılır pencere engelleyicisini kontrol ediniz.");
      return;
    }

    const htmlBody = textToPrintableHtml(content);
    const dateStr = new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="tr">
        <head>
          <meta charset="UTF-8">
          <title>HMK Koçluk - Öğrenci Değerlendirme Raporu</title>
          <style>
            @page { size: A4; margin: 18mm; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              color: #1e293b;
              line-height: 1.6;
              font-size: 13px;
              margin: 0;
              padding: 10px;
            }
            .header-table {
              width: 100%;
              border-bottom: 2px solid #ea580c;
              padding-bottom: 12px;
              margin-bottom: 18px;
            }
            .logo-title {
              font-size: 19px;
              font-weight: 800;
              color: #ea580c;
            }
            .logo-sub {
              font-size: 11px;
              color: #64748b;
              font-weight: 600;
              text-transform: uppercase;
            }
            .meta-box {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 10px 14px;
              margin-bottom: 20px;
              display: table;
              width: 100%;
              box-sizing: border-box;
            }
            .meta-item {
              display: table-cell;
              font-size: 12px;
              color: #334155;
            }
            .meta-item strong {
              color: #0f172a;
            }
            .report-content {
              font-size: 13px;
              color: #334155;
            }
            .footer {
              margin-top: 35px;
              border-top: 1px solid #e2e8f0;
              padding-top: 12px;
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              color: #94a3b8;
            }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <table class="header-table">
            <tr>
              <td>
                <div class="logo-title">KOÇUM ONLINE / HMK EĞİTİM KOÇLUĞU</div>
                <div class="logo-sub">Türkiye Yüzyılı Maarif Modeli Destekli Eğitim Danışmanlığı</div>
              </td>
              <td style="text-align: right; font-size: 12px; color: #64748b;">
                <strong>Tarih:</strong> ${dateStr}<br/>
                <strong>Eğitim Danışmanı:</strong> ${teacherName}
              </td>
            </tr>
          </table>

          <div class="meta-box">
            <div class="meta-item"><strong>Eğitim Danışmanı:</strong> ${teacherName}</div>
            <div class="meta-item"><strong>Tarih:</strong> ${dateStr}</div>
            <div class="meta-item"><strong>Sistem:</strong> Koçum Online AI</div>
          </div>

          <div class="report-content">
            ${htmlBody}
          </div>

          <div class="footer">
            <span>Bu resmi değerlendirme belgesi Koçum Online AI Eğitim Danışmanlığı Sistemi üzerinden üretilmiştir.</span>
            <span>Danışman: ${teacherName}</span>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Düzenleme Modalı Açma
  const handleOpenEditModal = (text, index) => {
    setEditModalData({ index, text });
  };

  // Düzenlenen Metni Güncelleme
  const handleSaveEditedText = () => {
    if (!editModalData) return;
    const { index, text } = editModalData;
    setMessages(prev => prev.map((m, i) => i === index ? { ...m, text } : m));
    setEditModalData(null);
  };

  return (
    <>
      {/* 1. SAĞ ALT KÖŞE: YÜZEN BUTON (FAB) */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center">
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="group relative flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-500 text-white rounded-full shadow-2xl shadow-orange-500/40 hover:shadow-orange-500/60 hover:scale-105 active:scale-95 transition-all duration-300 border border-orange-400/40"
            title="AI Koçluk & Eğitim Danışmanı ile Sohbet Et"
          >
            <div className="relative">
              <BrainCircuit size={22} className="animate-pulse" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-white animate-ping" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-white" />
            </div>
            <span className="font-bold text-sm tracking-wide hidden sm:inline">AI Koçluk Danışmanı</span>

            {hasUnread && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full ring-2 ring-white animate-bounce" />
            )}
          </button>
        )}
      </div>

      {/* 2. SOHBET PENCERESİ */}
      {isOpen && (
        <div className="fixed bottom-4 right-3 sm:bottom-6 sm:right-6 w-[95vw] sm:w-[490px] h-[640px] max-h-[88vh] z-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-700/80 bg-slate-900 text-slate-100 backdrop-blur-xl animate-fade-in">
          
          {/* HEADER */}
          <div className="px-4 py-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700/80 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white shadow-md">
                <BrainCircuit size={20} />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-slate-900" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                  AI Koçluk Danışmanı
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 font-semibold border border-orange-500/30">
                    Maarif Modeli
                  </span>
                </h3>
                <p className="text-[11px] text-slate-400">
                  {students.length > 0 ? `${students.length} Öğrenci Verisi Bağlı` : 'Pedagojik Danışmanlık'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={handleResetClick}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Yeni Sohbet (Tüm geçmişi sil)"
              >
                <RotateCcw size={16} />
              </button>

              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Pencereyi Küçült"
              >
                <ChevronDown size={18} />
              </button>
            </div>
          </div>

          {/* SOHBET İÇERİK ALANI */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-slate-950/60">
            {messages.length === 0 ? (
              // KARŞILAMA VE HIZLI BAŞLANGIÇ
              <div className="space-y-4 pt-2">
                <div className="p-4 rounded-xl bg-gradient-to-b from-slate-800/80 to-slate-900/80 border border-slate-700/60 text-center space-y-2 shadow-sm">
                  <div className="w-12 h-12 rounded-2xl bg-orange-500/10 text-orange-400 flex items-center justify-center mx-auto border border-orange-500/20">
                    <Sparkles size={24} />
                  </div>
                  <h4 className="font-bold text-white text-sm">Merhaba Sayın {teacherName}! 👨‍🏫</h4>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Ben senin <b>Eğitim & Koçluk Asistanınım</b>. Öğrencilerinizin tüm deneme, soru ve program verilerine hakimim.
                    Bana dilediğiniz öğrenci için rapor, program veya pedagojik taktik sorabilirsiniz.
                  </p>
                </div>

                {/* Hızlı Komut Örnekleri */}
                <div className="space-y-2">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block px-1">
                    Örnek İstekler:
                  </span>
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      onClick={() => {
                        const sName = students[0]?.name || "Emir";
                        setInputText(`${sName} ile ilgili haftalık veli bülteni oluştur. Raporda deneme gidişatını öv, bu hafta geometriye ağırlık vermesini istediğimi belirt.`);
                      }}
                      className="p-2.5 text-left rounded-xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700 text-xs text-slate-200 hover:text-white flex items-center gap-2.5 transition-all group"
                    >
                      <FileText size={16} className="text-orange-400 group-hover:scale-110 transition-transform shrink-0" />
                      <span>{students[0]?.name || "Emir"} için detaylı veli raporu oluştur</span>
                    </button>

                    <button
                      onClick={() => {
                        const sName = students[0]?.name || "Emir";
                        setInputText(`${sName} için haftalık ders çalışma ve ödev programı taslağı öner.`);
                      }}
                      className="p-2.5 text-left rounded-xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700 text-xs text-slate-200 hover:text-white flex items-center gap-2.5 transition-all group"
                    >
                      <Calendar size={16} className="text-emerald-400 group-hover:scale-110 transition-transform shrink-0" />
                      <span>Eksiklere göre haftalık çalışma programı öner</span>
                    </button>

                    <button
                      onClick={() => {
                        handleSendMessage("Maarif Modeli'ne göre beceri temelli yeni nesil sorulara ve kavramsal düşünmeye nasıl yaklaşmalıyız?");
                      }}
                      className="p-2.5 text-left rounded-xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700 text-xs text-slate-200 hover:text-white flex items-center gap-2.5 transition-all group"
                    >
                      <BrainCircuit size={16} className="text-blue-400 group-hover:scale-110 transition-transform shrink-0" />
                      <span>Maarif Modeli ve Beceri Temelli Koçluk Taktikleri</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // MESAJ BALONLARI
              messages.map((msg, index) => {
                const isUser = msg.role === 'user';
                return (
                  <div
                    key={index}
                    className={`flex flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'} animate-fade-in`}
                  >
                    <div className="flex gap-2.5 max-w-[92%]">
                      {!isUser && (
                        <div className="w-7 h-7 rounded-lg bg-orange-600 flex items-center justify-center text-white shrink-0 mt-1 shadow-sm">
                          <BrainCircuit size={15} />
                        </div>
                      )}

                      <div
                        className={`rounded-2xl p-3.5 shadow-sm text-sm ${
                          isUser
                            ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-br-xs'
                            : msg.isError
                            ? 'bg-red-950/40 border border-red-500/30 text-red-200 rounded-bl-xs'
                            : 'bg-slate-800/90 border border-slate-700/60 text-slate-100 rounded-bl-xs'
                        }`}
                      >
                        {isUser ? (
                          <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                        ) : (
                          <FormattedMessage content={msg.text} />
                        )}

                        <div className={`text-[10px] mt-1.5 text-right ${isUser ? 'text-white/70' : 'text-slate-400'}`}>
                          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </div>

                    {/* ASİSTAN CEVAPLARI İÇİN HIZLI AKSİYON BARI (PDF / KOPYALA / DÜZENLE) */}
                    {!isUser && !msg.isError && (
                      <div className="flex items-center gap-1.5 ml-9 text-xs">
                        {/* PDF İndir / Yazdır */}
                        <button
                          type="button"
                          onClick={() => handlePrintPdf(msg.text)}
                          className="px-2.5 py-1 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700/80 flex items-center gap-1.5 transition-colors shadow-xs"
                          title="Resmi PDF Raporu Olarak Yazdır / Kaydet"
                        >
                          <Printer size={13} className="text-orange-400" />
                          <span>PDF Yazdır</span>
                        </button>

                        {/* Düzenle & Yazdır */}
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(msg.text, index)}
                          className="px-2.5 py-1 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700/80 flex items-center gap-1.5 transition-colors shadow-xs"
                          title="Raporu veya metni düzenle"
                        >
                          <Edit3 size={13} className="text-amber-400" />
                          <span>Düzenle</span>
                        </button>

                        {/* Metni Kopyala */}
                        <button
                          type="button"
                          onClick={() => handleCopyText(msg.text, index)}
                          className="px-2.5 py-1 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700/80 flex items-center gap-1.5 transition-colors shadow-xs"
                          title="Metni kopyala (WhatsApp / Mesaj için hazır)"
                        >
                          {copyFeedbackIndex === index ? (
                            <>
                              <Check size={13} className="text-emerald-400" />
                              <span className="text-emerald-400">Kopyalandı!</span>
                            </>
                          ) : (
                            <>
                              <Copy size={13} />
                              <span>Kopyala</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* YÜKLENİYOR ANİMASYONU */}
            {isLoading && (
              <div className="flex gap-2.5 justify-start animate-fade-in">
                <div className="w-7 h-7 rounded-lg bg-orange-600 flex items-center justify-center text-white shrink-0 mt-1">
                  <BrainCircuit size={15} />
                </div>
                <div className="p-3.5 rounded-2xl rounded-bl-xs bg-slate-800/90 border border-slate-700/60 flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-orange-400" />
                  <span className="text-xs text-slate-300 font-medium">Öğrenci verileri inceleniyor, yanıt hazırlanıyor...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* INPUT ALANI */}
          <div className="p-3 bg-slate-900 border-t border-slate-700/80">
            <div className="flex items-center gap-2 bg-slate-800/90 border border-slate-700 rounded-xl px-2.5 py-1.5 focus-within:border-orange-500 transition-colors">
              <input
                ref={textInputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Örn: Emir ile ilgili veli raporu oluştur, bu hafta geometriye odaklansın..."
                disabled={isLoading}
                className="flex-1 bg-transparent text-xs sm:text-sm text-slate-100 placeholder-slate-400 focus:outline-none py-1"
              />

              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputText.trim()}
                className="p-2 rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:hover:bg-orange-600 text-white transition-all shrink-0"
                title="Gönder"
              >
                <Send size={15} />
              </button>
            </div>

            <div className="mt-1.5 text-[10px] text-center text-slate-400 truncate">
              Öğrenci adı belirterek rapor, haftalık ödev programı veya pedagojik taktik isteyebilirsiniz.
            </div>
          </div>
        </div>
      )}

      {/* 3. DÜZENLEME & PDF YAZDIRMA MODALI */}
      {editModalData && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700/90 rounded-2xl p-5 max-w-2xl w-full shadow-2xl flex flex-col max-h-[85vh] space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <Edit3 size={18} className="text-orange-400" />
                <h3 className="text-sm font-bold text-white">Metni Düzenle & Çıktı Al</h3>
              </div>
              <button
                type="button"
                onClick={() => setEditModalData(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-slate-400">
              Metin üzerinde istediğiniz cümleleri ekleyip çıkarabilirsiniz. Değişiklikler sohbet pencerenize ve PDF çıktısına yansıyacaktır.
            </p>

            <textarea
              value={editModalData.text}
              onChange={(e) => setEditModalData({ ...editModalData, text: e.target.value })}
              rows={14}
              className="w-full flex-1 bg-slate-950 border border-slate-700 rounded-xl p-3 text-xs sm:text-sm text-slate-100 font-mono leading-relaxed focus:outline-none focus:border-orange-500 resize-none custom-scrollbar"
            />

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setEditModalData(null)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
              >
                İptal
              </button>

              <button
                type="button"
                onClick={() => handlePrintPdf(editModalData.text)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-orange-400 hover:text-orange-300 border border-orange-500/30 text-xs font-bold flex items-center gap-1.5"
              >
                <Printer size={14} />
                <span>Düzenlenmiş PDF İndir</span>
              </button>

              <button
                type="button"
                onClick={handleSaveEditedText}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-md shadow-orange-500/25"
              >
                <Save size={14} />
                <span>Kaydet</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. YENİ SOHBET ONAY MODALI */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4 text-center animate-scale-in">
            <div className="w-12 h-12 rounded-2xl bg-orange-500/15 text-orange-400 flex items-center justify-center mx-auto border border-orange-500/30">
              <RotateCcw size={22} />
            </div>

            <div className="space-y-1.5">
              <h3 className="text-base font-bold text-white tracking-wide">
                Yeni Sohbet Başlatılsın mı?
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed px-2">
                Tüm danışmanlık ve raporlama sohbet geçmişiniz temizlenecektir.
              </p>
            </div>

            <div className="flex items-center gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs sm:text-sm font-semibold transition-all border border-slate-700 hover:border-slate-600"
              >
                Vazgeç
              </button>

              <button
                type="button"
                onClick={executeResetChat}
                className="flex-1 py-2.5 px-4 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-orange-500/25 transition-all flex items-center justify-center gap-1.5"
              >
                <span>Evet, Temizle</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
